import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Collapse,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import SidebarMenuItem from './SidebarMenuItem';
import AppLogo from './AppLogo';
import SproutIcon from '../Common/SproutIcon';
import { ChevronRight } from '../Common/icons';
import { NAV_SECTIONS, OVERVIEW_ITEM, matchesPath } from '../../config/navigation';

const DRAWER_WIDTH = 280;

const groupContainsPath = (group, pathname) => group.items.some((item) => matchesPath(pathname, item.path));

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    component="div"
    sx={{ px: 1.5, pt: 2.5, pb: 0.75, color: 'text.secondary', display: 'block' }}
  >
    {children}
  </Typography>
);

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

const GroupToggle = ({ group, open, onToggle, active }) => (
  <ListItem disablePadding sx={{ mb: 0.25 }}>
    <ListItemButton
      onClick={onToggle}
      aria-expanded={open}
      sx={(theme) => ({
        minHeight: 36,
        py: 0.75,
        px: 1.5,
        gap: 1.25,
        color: active ? theme.palette.sprout.onSurface : theme.palette.sprout.supportingText,
        '&:hover': { backgroundColor: theme.palette.sprout.surfaceHovered, color: theme.palette.sprout.onSurface },
      })}
    >
      <Box sx={{ display: 'flex' }}>
        <SproutIcon icon={group.icon} size={18} />
      </Box>
      <ListItemText
        primary={group.title}
        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 700 : 600, color: 'inherit', noWrap: true }}
      />
      <Box
        sx={{
          display: 'flex',
          transition: 'transform 0.15s ease',
          transform: open ? 'rotate(90deg)' : 'none',
        }}
      >
        <SproutIcon icon={ChevronRight} size={12} />
      </Box>
    </ListItemButton>
  </ListItem>
);

GroupToggle.propTypes = {
  group: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  active: PropTypes.bool,
};

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  // Expand the group that owns the current route (e.g. after a deep link).
  useEffect(() => {
    NAV_SECTIONS.forEach((section) =>
      section.groups.forEach((group) => {
        if (groupContainsPath(group, location.pathname)) {
          setOpenGroups((prev) => (prev[group.id] ? prev : { ...prev, [group.id]: true }));
        }
      })
    );
  }, [location.pathname]);

  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleNavigate = () => {
    if (onClose) onClose();
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, height: 64, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <AppLogo size={32} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.9375rem', lineHeight: 1.2 }}>Aceve Platform</Typography>
          <Typography variant="body2" color="text.secondary">
            Web Components
          </Typography>
        </Box>
      </Box>

      <Box component="nav" aria-label="Component navigation" sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        <List disablePadding sx={{ pt: 1 }}>
          <SidebarMenuItem item={OVERVIEW_ITEM} onNavigate={handleNavigate} />
        </List>

        {NAV_SECTIONS.map((section) => (
          <List key={section.id} disablePadding>
            <SectionLabel>{section.title}</SectionLabel>

            {section.playground && (
              <SidebarMenuItem
                item={section.playground}
                onNavigate={handleNavigate}
                isSelected={location.pathname.startsWith(section.playground.route.replace('/*', ''))}
              />
            )}

            {section.groups.length === 1
              ? section.groups[0].items.map((item) => (
                  <SidebarMenuItem key={item.path} item={item} onNavigate={handleNavigate} />
                ))
              : section.groups.map((group) => {
                  const isOpen = Boolean(openGroups[group.id]);
                  return (
                    <React.Fragment key={group.id}>
                      <GroupToggle
                        group={group}
                        open={isOpen}
                        onToggle={() => toggleGroup(group.id)}
                        active={groupContainsPath(group, location.pathname)}
                      />
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List disablePadding>
                          {group.items.map((item) => (
                            <SidebarMenuItem key={item.path} item={item} level={1} onNavigate={handleNavigate} />
                          ))}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
          </List>
        ))}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Built by OnePlatform Services
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Sidebar;
export { DRAWER_WIDTH };
